export const en = {
  shell: {
    language: {
      label: 'Language',
      english: 'English',
      simplifiedChinese: 'Simplified Chinese',
    },
    theme: {
      switchTo: 'Switch to {{mode}} mode',
      light: 'light',
      dark: 'dark',
    },
    nav: {
      openNavigation: 'Open navigation',
      closeNavigation: 'Close navigation',
      mobileNavigation: 'Mobile navigation',
      home: 'Home',
      tools: 'Tools',
      apps: 'Apps',
      benchmarks: 'Benchmarks',
      blog: 'Blog',
      devDocs: 'Dev Docs',
      download: 'Download',
      downloadDesktopApps: 'Download desktop apps',
      signUp: 'Sign Up',
      selfHost: 'Self-host runtime on GitHub',
      soon: 'Soon',
      browseAllTools: 'Browse all {{count}} tools',
      browseDownloads: 'All downloads',
    },
    apps: {
      macos: {
        title: 'Desktop for Mac',
        body: 'Apple silicon Mac, macOS 13 or later. release candidate.',
      },
      windows: {
        title: 'Desktop for Windows',
        body: 'Windows 10 or later, x64. alpha.',
      },
      chrome: {
        title: 'Chrome extension',
        body: 'Markdown, tables and an accessibility check for the PDF in your tab.',
      },
    },
    categoryBody: {
      accessibility: 'PDF/UA, Matterhorn & tagged-PDF checks.',
      organize: 'Merge, split, and reorder pages.',
      edit: 'Redact, sign, watermark, rotate.',
      convertTo: 'Excel, Word, and images to PDF.',
      convertFrom: 'PDF to Markdown, JSON, Excel, images.',
      optimize: 'Compress, OCR, flatten, compare.',
    },
    footer: {
      description:
        'PDF to document intelligence with AI agents. TypeScript SDK, open-source CLI, and a self-host runtime path.',
      products: 'Products',
      tools: 'Tools',
      resources: 'Resources',
      parse: 'Parse',
      extract: 'Extract',
      pdfAgents: 'PDF agents',
      pdfMcp: 'PDF MCP',
      pdfCli: 'PDF CLI',
      selfHost: 'Self-host',
      workflows: 'Workflows',
      audit: 'A11y Audit',
      pdfToFigma: 'PDF to Figma',
      chromeExtension: 'Chrome extension',
      mergePdf: 'Merge PDF',
      splitPdf: 'Split PDF',
      compressPdf: 'Compress PDF',
      pdfToWord: 'PDF to Word',
      pdfToExcel: 'PDF to Excel',
      pdfToHtml: 'PDF to HTML',
      pdfToMarkdown: 'PDF to Markdown',
      allTools: 'All tools',
      benchmark: 'Benchmarks',
      github: 'GitHub',
      discord: 'Discord',
      industries: 'Industries',
      privacy: 'Privacy',
      terms: 'Terms',
    },
  },
  home: {
    meta: {
      title: 'okraPDF: PDF to document intelligence with AI agents',
      description:
        'PDF to document intelligence with AI agents. Use okraPDF to parse, cite, extract, host, review, and self-host PDF workflows with a TypeScript SDK, CLI, API, and MCP surface.',
    },
    hero: {
      badge: 'Agentic PDF intelligence',
      titleBefore: 'PDF to document intelligence with',
      titleAccent: 'AI agents',
      body: 'Plan, parse, cite, and route PDF work automatically from Claude, Codex, Cursor, MCP, CLI, and API workflows. okraPDF gives agents a TypeScript document graph with page evidence, typed extraction, and production handoffs.',
      startFree: 'Start for $0',
      discordSupport: 'Discord Support',
      agenticLink: 'I need agentic PDF intelligence',
      normalToolsLink: 'I need normal PDF tools',
      selfHostLink: 'I need self-host',
      runsInside: 'Runs inside',
      videoLabel:
        'Animated okraPDF document workflow: a sample invoice moves from page preview to template extraction to clean JSON, CSV, or webhook export.',
    },
    audience: {
      kicker: 'Who is okraPDF for?',
      heading: 'Teams turning PDFs into agent-ready work.',
      cards: {
        agentic: {
          title: 'Agentic',
          body: 'Prompt Claude, Codex, Cursor, or your own worker. okraPDF gives the agent cited PDF context, typed fields, and actions it can run.',
        },
        developers: {
          title: 'Developers',
          body: 'Build PDF-native products on a TypeScript SDK, REST API, CLI, and MCP-friendly resource catalog. One document graph feeds every surface.',
        },
        automation: {
          title: 'Automation teams',
          body: 'Route invoices, statements, forms, and review packets into webhooks, n8n, Make, Zapier, Sheets, or a human approval queue.',
        },
      },
    },
    workflow: {
      kicker: 'Agent workflows',
      heading: 'Run the PDF loop end to end: parse, ask, extract, review, publish.',
      body: 'okraPDF is the document intelligence layer under your agent. It gives each PDF a durable graph, proof links, typed contracts, and workflow actions across the same API, CLI, and MCP surface.',
      features: {
        graph: {
          label: 'Document graph',
          detail:
            'Upload once. Agents read pages, tables, images, artifacts, and prior runs from the same document ID.',
        },
        proof: {
          label: 'Page proof',
          detail:
            'Every cited answer carries the page, quote, bbox, and proof URL needed to open the exact source region.',
        },
        fields: {
          label: 'Typed fields',
          detail:
            'Use invoice, statement, contract, or custom schemas; ungrounded fields are routed to review instead of invented.',
        },
        handoff: {
          label: 'Workflow handoff',
          detail:
            'Send results to webhooks, n8n, Make, Zapier, Sheets, or a human approval gate without rewriting the parse.',
        },
      },
    },
    toolset: {
      kicker: 'AI and automation',
      heading: 'All the tools required for agentic PDF intelligence in one place.',
      body: 'The same document graph powers the hosted app, API keys, CLI, MCP tools, and workflow automations, so agents do not have to stitch together separate PDF utilities.',
      items: {
        parse: {
          label: 'Parse',
          body: 'Turn raw PDFs into page-aware nodes with text, tables, images, and layout metadata.',
        },
        context: {
          label: 'Context',
          body: 'Give agents token-bounded answers with page, quote, bbox, and proof URLs attached.',
        },
        extract: {
          label: 'Extract',
          body: 'Map PDFs into typed JSON schemas and content-type contracts your app already understands.',
        },
        host: {
          label: 'Host',
          body: 'Publish permissioned PDF links, previews, and artifacts from the same document ID.',
        },
        review: {
          label: 'Review',
          body: 'Open durable human gates for extraction, accessibility, redaction, and publish decisions.',
        },
        convert: {
          label: 'Convert',
          body: 'Use everyday PDF utilities when an agent needs HTML, images, Markdown, CSV, or Excel.',
        },
      },
    },
    developer: {
      kicker: 'Developer benefits',
      heading: 'Open-source where it should be. Self-host where it matters.',
      body: 'okraPDF is TypeScript-native first: SDK, CLI, schemas, recipes, and a local runtime path for teams that need control over document data.',
      explore: 'Explore self-host',
      benefits: {
        openSource: {
          kicker: 'Open-source',
          title: 'SDK and CLI you can inspect',
          body: 'The agent-facing TypeScript packages are MIT-licensed, versioned, and built around JSON envelopes with next actions.',
          cta: 'View GitHub',
        },
        selfHost: {
          kicker: 'Self-host',
          title: 'Run a local document-graph runtime',
          body: 'The Docker runtime ships Node 22, Poppler, Tesseract, filesystem state, and basic parse, audit, and redact recipes.',
          cta: 'View self-host runtime',
        },
        typescript: {
          kicker: 'TypeScript',
          title: 'Schemas agents can rely on',
          body: 'Zod contracts, typed SDK methods, and content-type manifests keep parse results stable across CLI, API, and MCP.',
          cta: 'Read docs',
        },
        community: {
          kicker: 'Community',
          title: 'Bring hard PDFs to Discord',
          body: 'Use the community to debug parser edge cases, self-host setup, agent workflows, and integration recipes.',
          cta: 'Join Discord',
        },
      },
    },
    industries: {
      kicker: 'Industries',
      heading: 'Built for the documents your industry runs on.',
      body: 'Start from the PDFs your team already processes, then route the extracted rows into the systems each workflow depends on.',
      seeAll: 'See all industries',
      cards: {
        accounting: {
          name: 'Accounting & bookkeeping',
          tagline: 'Invoices, receipts, statements — all extracted, all tagged.',
        },
        logistics: {
          name: 'Logistics & shipping',
          tagline: 'BoLs, packing slips, POs — extracted, tracked, paid on time.',
        },
        legal: {
          name: 'Legal services',
          tagline: 'Contracts, court filings, redactions — searchable, citable, safe.',
        },
        realEstate: {
          name: 'Real estate & title',
          tagline: 'Closing docs, insurance, title work — typed, indexed, archived.',
        },
      },
    },
    proof: {
      points: {
        one: 'One PDF can become cited agent context, typed JSON, hosted links, workflow events, or review tasks.',
        two: 'Every answer keeps page evidence attached, so your agent can cite the source instead of guessing.',
        three:
          'Start hosted, then move deterministic workloads to the self-host runtime when privacy demands it.',
      },
      integrations: 'Browse integration guides',
    },
  },
  tools: {
    index: {
      categoriesLabel: 'Filter by category',
      metaTitle: 'Free PDF Tools — Edit, Convert & Organize | okraPDF',
      metaDescription:
        'Free PDF tools to merge, split, convert, compress, OCR, and check accessibility. Browser tools need no signup; cloud tools need an okraPDF account.',
      h1: 'Free PDF tools',
      subhead: 'Edit, convert, organize, and check PDFs — free and browser-first.',
      searchLabel: 'Search PDF tools',
      searchPlaceholder: 'Search PDF/UA, WCAG, merge...',
      keywordPicks: 'Start with one of these',
      editorCard: {
        title: 'Edit a PDF',
        body: 'Change the text in a PDF, add new text, sign it, and fix its pages. Free, in your browser.',
        cta: 'Open the PDF editor',
      },
    },
    categories: {
      all: 'All Tools',
      organize: 'Organize PDF',
      edit: 'Edit PDF',
      accessibility: 'Accessibility checks',
      convertTo: 'Convert to PDF',
      convertFrom: 'Convert from PDF',
      optimize: 'Optimize PDF',
    },
    grid: {
      cloud: 'Cloud',
      results_one: '{{count}} tool found',
      results_other: '{{count}} tools found',
      reset: 'Clear filters',
      cloudAi: 'Cloud AI',
      compliance: 'Compliance',
      onDevice: 'On-device',
      browserTitle: 'Runs in your browser — the file never leaves your device',
      openTool: 'Open tool',
      useTool: 'Use tool',
      noMatch: 'No tools match "{{query}}".',
      popular: 'Popular tools',
      popularSubhead: 'The tools people reach for most.',
      accessibilitySubhead: 'PDF/UA, Matterhorn, tagged-PDF signals, and human-review checks.',
      organizeSubhead: 'Merge, split, and reorder pages on-device.',
      editSubhead: 'Redact, sign, watermark, and annotate without uploading.',
      convertToSubhead: 'Turn Word, Excel, and images into PDF.',
      convertFromSubhead: 'Export PDFs to Markdown, Word, Excel, JSON, HTML, and more.',
      optimizeSubhead: 'Compress, flatten, OCR, and inspect for sharing and storage.',
      count: '{{count}} tool',
      count_other: '{{count}} tools',
    },
    detail: {
      allTools: 'All Tools',
      toolAria: '{{name}} tool',
      fallbackAria: 'PDF tool',
      cloudProcessing: 'Cloud processing',
      cloudExtraction: 'Cloud extraction',
      browserOnly: 'Browser-only',
      howTo: 'How to use {{name}}',
      faq: 'Frequently asked questions',
    },
    uploader: {
      chooseFile: 'Choose file',
      chooseFiles: 'Choose files',
      invalidPdf: '{{name}} is not a valid PDF.',
      unsupported: 'Unsupported file type: {{name}}',
      dropHere: 'Drop here',
      clickOrDrop: 'Click to upload or drag and drop',
      multipleDescription: 'Add one or more PDF files',
      singleDescription: 'Upload a PDF document',
    },
    runners: {
      merge: {
        addTitle: 'Add PDFs or images',
        addDescription: 'PDF, JPG, PNG, WebP, GIF — images become single PDF pages',
        unsupported: 'Unsupported file type: {{names}}. Use PDF, JPG, PNG, WebP, or GIF.',
        addFailed: 'Could not add {{name}}: {{reason}}',
        unreadable: 'unreadable file',
        mergeFailed: 'Merge failed: {{reason}}',
        unexpected: 'unexpected error',
        converting: 'Converting {{count}} image',
        converting_other: 'Converting {{count}} images',
        summary: '{{files}} files · {{pages}} pages',
        clearAll: 'Clear all',
        pageCount: '{{count}} page',
        pageCount_other: '{{count}} pages',
        moveUp: 'Move up',
        moveDown: 'Move down',
        remove: 'Remove',
        merging: 'Merging…',
        action: 'Merge {{count}} files',
        needMore: 'Add at least one more file to merge',
        done: 'Merged. Download started.',
      },
      pdfToExcel: {
        readFailed: 'Could not read PDF',
        noText: 'No tables found in this PDF.',
        conversionFailed: 'Conversion failed',
        uploadTitle: 'Drop your PDF',
        uploadDescription: 'LlamaParse reads the tables, in digital and scanned PDFs',
        pageSummary: '{{count}} pages',
        changeFile: 'Change file',
        extracting: 'Extracting tables…',
        action: 'Convert & download .xlsx',
        done: 'Converted. Download started.',
        caveat: 'One sheet per table.',
      },
      wordToPdf: {
        docxRequired:
          'Upload a .docx Word document. Older .doc files need to be saved as .docx first.',
        readFailed: 'Could not read .docx',
        conversionFailed: 'Conversion failed',
        uploadTitle: 'Drop your DOCX document',
        uploadDescription: 'Supports .docx Word documents',
        characterCount: '{{count}} characters extracted',
        changeFile: 'Change file',
        preview: 'Preview',
        empty: '(empty)',
        converting: 'Converting…',
        action: 'Convert & download PDF',
        done: 'Converted. Download started.',
      },
      pdfToJson: {
        copy: 'copy',
        copied: 'copied',
        phases: {
          idle: 'Waiting',
          uploading: 'Uploading',
          processing: 'Parsing',
          ready: 'Ready',
          extracting: 'Extracting',
          done: 'Done',
          error: 'Needs attention',
        },
        steps: {
          upload: 'Upload',
          parse: 'Parse',
          schema: 'Suggest',
          confirm: 'Confirm',
          extract: 'Extract',
        },
        presets: {
          document: {
            name: 'Document',
            description: 'Title, author, sections, entities, dates, tables, and key passages.',
          },
          veryfi: {
            name: 'Invoice',
            description: 'Vendor, totals, dates, payment terms, and line items.',
          },
          timesheet: {
            name: 'Timesheet',
            description: 'Employee identity, daily rows, signatures, and pay summary.',
          },
          bank: {
            name: 'Bank',
            description: 'Statement period, balances, and transaction rows.',
          },
        },
        status: {
          uploadPdf: 'Upload a PDF',
          reviewSchema: 'Review suggested schema',
          suggestingSchema: 'Suggesting schema',
          parsing: 'Parsing with Gemini Flash',
          jsonReady: 'JSON ready',
          creatingSession: 'Creating fresh file session',
          startingWorkflow: 'Uploading and starting workflow',
          queued: 'Workflow queued',
          confirmingSchema: 'Confirming schema',
          extractingJson: 'Extracting JSON',
        },
        generatedSchema: 'Generated from this PDF.',
        noPdf: 'No PDF selected',
        reset: 'Reset',
        replacePdf: 'Replace PDF',
        uploadPdf: 'Upload PDF',
        confirmSchema: 'Confirm schema',
        localPreview: 'Local preview while the file uploads',
        clearFile: 'Clear selected file',
        previewTitle: 'Uploaded PDF preview',
        dropPdf: 'Drop the PDF',
        uploadPrompt: 'Upload a PDF',
        uploadDescription:
          'The preview opens immediately. Uploads use a fresh file session, so replacing a PDF does not hit the old one-file limit.',
        choosePdf: 'Choose PDF',
        fileLimit: 'PDF only, 5MB max',
        suggestedSchema: 'Suggested schema',
        events: '{{count}} event',
        events_other: '{{count}} events',
        clear: 'Clear',
        agent: 'Agent',
        extractionOutput: 'Extraction output',
        resultMatched: 'Result matched the active schema.',
        jsonReady: 'JSON ready',
        extractedFile: '{{name}} extracted with this schema.',
        extractedGeneric: 'Extraction completed with this schema.',
        startFree: 'Start for $0',
        cost: 'Cost',
        duration: 'Duration',
        extractsLeft: 'Guest extracts left',
        limitTitle: 'Free extraction limit reached',
        limitDescription: 'Open Studio to keep working with PDF tools in one place.',
        unlimited: 'Studio',
        confirmTitle: 'Confirm JSON schema',
        keepEditing: 'Keep editing',
        confirmAndExtract: 'Confirm and extract',
      },
    },
  },
} as const;

export type TranslationResource = typeof en;
